import { useAtom } from "jotai";
import { useEffect, useState } from "react";

import { useDesignProjectSync } from "@design-lab/features/design-project";
import { useValidation } from "@design-lab/shared/hooks/useValidation";
import { useEditingStoreId } from "@design-lab/shared/lib/config";
import { shopifyRecipeLoadingAtom } from "@design-lab/shared/state";

import { useCreateShopifyRecipe } from "./useCreateShopifyRecipe";
import { useShopifyStores } from "./useShopifyStores";

type UseShopifyRecipeFormParams = {
  isAnyLoading: boolean;
};

/**
 * Manages shared form state and logic for Shopify recipe creation/editing.
 *
 * Encapsulates store selection, validation, disabled state computation,
 * and submission handling used by both desktop and mobile recipe buttons.
 *
 * @param params.isAnyLoading - External loading state from parent component
 * @returns Form state and handlers for Shopify recipe UI
 */
export function useShopifyRecipeForm({ isAnyLoading }: UseShopifyRecipeFormParams) {
  const { stores, isLoading: isLoadingStores } = useShopifyStores();
  const {
    createShopifyRecipe,
    isLoading: isCreatingShopifyRecipe,
    isEditing
  } = useCreateShopifyRecipe();
  const [isShopifyRecipeLoading] = useAtom(shopifyRecipeLoadingAtom);
  const { isSaving } = useDesignProjectSync();
  const editingStoreId = useEditingStoreId();
  const [selectedStoreId, setSelectedStoreId] = useState<string>("");
  const validation = useValidation();

  useEffect(() => {
    // When editing, use the store from the recipe
    if (editingStoreId) {
      setSelectedStoreId(editingStoreId);
      return;
    }
    // Otherwise default to first store
    const firstStore = stores[0];
    if (firstStore && !selectedStoreId) {
      setSelectedStoreId(firstStore.id);
    }
  }, [stores, selectedStoreId, editingStoreId]);

  const handleSubmit = async () => {
    if (!selectedStoreId) return;
    await createShopifyRecipe(selectedStoreId);
  };

  const hasNoStores = !isLoadingStores && stores.length === 0;

  const isDisabled =
    isAnyLoading ||
    isShopifyRecipeLoading ||
    isSaving ||
    hasNoStores ||
    !selectedStoreId ||
    !validation.isArtworkUploaded ||
    validation.isSomeArtworkLoading;

  const isSelectDisabled = isAnyLoading || isLoadingStores || hasNoStores || isEditing;

  const noStoresMessage = "You are not an admin of any Shopify store";
  const tooltipTitle = hasNoStores ? noStoresMessage : undefined;

  return {
    stores,
    selectedStoreId,
    setSelectedStoreId,
    isCreatingShopifyRecipe,
    isEditing,
    handleSubmit,
    hasNoStores,
    isDisabled,
    isSelectDisabled,
    tooltipTitle
  };
}
